import { c as _c } from "react/compiler-runtime";
import XIcon from '@eversports/design-tokens/assets/icons/regular/xmark.svg?icon';
import SuccessIcon from '@eversports/design-tokens/assets/icons/solid/check.svg?icon';
import { cn } from '@eversports/klimt-design-components/tailwind/utils';
import { Branch as DismissableLayerBranch } from '@radix-ui/react-dismissable-layer';
import { cva, type VariantProps } from 'class-variance-authority';
import { ComponentType, ReactNode, SVGProps, useEffect, useState } from 'react';
export interface SnackbarSuccessProps extends VariantProps<typeof bodyVariants> {
  variant: 'success';
  message: ReactNode;
  onClose?: () => void;
  timeout?: number;
  dataTestId?: string;
}
export interface SnackbarErrorProps extends VariantProps<typeof bodyVariants> {
  variant: 'error';
  message: ReactNode;
  onClose?: () => void;
  dataTestId?: string;
}
type Props = SnackbarSuccessProps | SnackbarErrorProps;
const bodyVariants = cva('bg-surface-dark-hover text-text-white gap-2 rounded-md p-2 pointer-events-auto flex w-fit flex-row items-center border-2 transition-opacity', {
  variants: {
    variant: {
      success: 'border-border-success',
      error: 'border-border-error'
    }
  }
});
const iconVariants = cva('size-2 p-0.5 rounded-full', {
  variants: {
    variant: {
      success: 'bg-surface-success',
      error: 'bg-surface-error'
    }
  }
});
type IconType = ComponentType<SVGProps<SVGSVGElement>>;
const IconMapping = {
  success: SuccessIcon as IconType,
  error: XIcon as IconType
};
export const DEFAULT_SNACKBAR_TIMEOUT = 6000;
export default function Snackbar(props) {
  const $ = _c(15);
  const [show, setShow] = useState(true);
  const [mounted, setMounted] = useState(true);
  const timeout = props.variant !== "error" ? props.timeout || DEFAULT_SNACKBAR_TIMEOUT : null;
  let t0;
  if ($[0] !== props || $[1] !== timeout) {
    t0 = () => {
      if (!timeout) {
        return;
      }
      let timer = null;
      timer = setTimeout(() => {
        setShow(false);
        setTimeout(() => {
          setMounted(false);
        }, 150);
        if (props.onClose) {
          props.onClose();
        }
      }, timeout);
      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    };
    $[0] = props;
    $[1] = timeout;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props.message) {
    t1 = [props.message];
    $[3] = props.message;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] !== props) {
    t2 = () => {
      setShow(false);
      if (props.onClose) {
        props.onClose();
      }
      setTimeout(() => {
        setMounted(false);
      }, 150);
    };
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleClose = t2;
  if (!props.variant) {
    return null;
  }
  const Icon = IconMapping[props.variant];
  let t3;
  if ($[7] !== Icon || $[8] !== handleClose || $[9] !== mounted || $[10] !== props.dataTestId || $[11] !== props.message || $[12] !== props.variant || $[13] !== show) {
    t3 = mounted && <DismissableLayerBranch className="inset-x-0 bottom-0 pb-2 pointer-events-none fixed z-99999999999 flex justify-center" data-testid={props.dataTestId}><div className={cn(bodyVariants({
        variant: props.variant
      }), show ? "animate-in fade-in slide-in-from-bottom-8 opacity-100" : "animate-out fade-out slide-out-to-bottom-8 opacity-0")}><div className="gap-1 flex items-center"><Icon className={iconVariants({
            variant: props.variant
          })} />{props.message}</div><XIcon className="size-2 cursor-pointer" onClick={handleClose} /></div></DismissableLayerBranch>;
    $[7] = Icon;
    $[8] = handleClose;
    $[9] = mounted;
    $[10] = props.dataTestId;
    $[11] = props.message;
    $[12] = props.variant;
    $[13] = show;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}